import { useAuth0 } from '@auth0/auth0-react';
import { ROUTER_NAME } from '@consts';
import * as Sentry from '@sentry/nextjs';
import { useRouter } from 'next/router';
import Script from 'next/script';
import { ReactElement } from 'react';

/**
 * CookieBanner component that displays Cookiebot consent banner
 * only for unauthenticated users on specific pages.
 *
 * For authenticated users, it ensures all Cookiebot elements are hidden
 * across the entire application.
 */
export default function CookieBanner(): ReactElement | null {
  const { isAuthenticated } = useAuth0();
  const router = useRouter();

  const isCookiePage = [ROUTER_NAME.EXPLORE, ROUTER_NAME.GOAL, ROUTER_NAME.GOAL_THANK_YOU].includes(
    router.pathname
  );

  // Hide Cookiebot elements for authenticated users globally
  // We also hide them on pages where the banner shouldn't be visible
  if (isAuthenticated || !isCookiePage) {
    return (
      <style jsx global>{`
        #CybotCookiebotDialog,
        #CybotCookiebotDialogBody,
        #CookiebotWidget,
        div[id^='Cookiebot'],
        div[id^='CybotCookiebot'] {
          display: none !important;
        }
      `}</style>
    );
  }

  const cookiebotId = process.env.NEXT_PUBLIC_COOKIEBOT_CBID;
  if (!cookiebotId) {
    Sentry.captureMessage('NEXT_PUBLIC_COOKIEBOT_CBID environment variable is not set');
    return null;
  }

  return (
    <>
      <Script
        id='gtag-consent'
        data-cookieconsent='ignore'
        strategy='afterInteractive'
        dangerouslySetInnerHTML={{
          __html: `
            window.dataLayer = window.dataLayer || [];
            function gtag() {
              dataLayer.push(arguments);
            }
            
            gtag("consent", "default", {
              ad_personalization: "denied",
              ad_storage: "denied",
              ad_user_data: "denied",
              analytics_storage: "denied",
              functionality_storage: "denied",
              personalization_storage: "denied",
              security_storage: "granted",
              wait_for_update: 500,
            });
            gtag("set", "ads_data_redaction", true);
            gtag("set", "url_passthrough", false);
          `
        }}
      />
      <Script
        id='Cookiebot'
        src='https://consent.cookiebot.com/uc.js'
        data-cbid={cookiebotId}
        data-blockingmode='auto'
        strategy='afterInteractive'
      />
    </>
  );
}
